/* eslint-disable import/no-cycle */
import { IAlert } from './Alert';
import { IApiList } from './Api';
import { CalculatorParticipantType } from './CalculatorRule';
import { ICardDetails } from './Card';
import { DisbursementMethod, EmployeeStatus, EventActionTypes, IEmployee } from './Employee';
import { IJob } from './Job';
import { IBaseSite } from './Site';
import { ISubmittedBy } from './User';

export enum PayoutStatus {
  PAID = 'paid',
  PENDING = 'pending',
  EXPIRED = 'expired',
  CANCELED = 'canceled',
  PAYROLL = 'payroll',
}

export enum PayoutApprovalStatus {
  AWAITING_CHECKS = 'awaiting_checks',
  NOT_REQUIRED = 'not_required',
  REVIEW_REQUIRED = 'review_required',
  REVIEWER_APPROVED = 'reviewer_approved',
  REVIEWER_REJECTED = 'reviewer_rejected',
}

export enum PayoutApprovalFailedCheckStatus {
  LAST_PAYMENT = 'lastPayment',
  DAILY_VELOCITY_LIMIT = 'dailyVelocityLimit',
}

export enum CurrencyType {
  USD = 'usd',
}

export enum PaymentType {
  TIP = 'tip',
}

export enum PayoutRefType {
  ACCEPTANCE = 'acceptance',
  CALCULATOR = 'calculator',
}

export interface IPayoutEmployee {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  status: EmployeeStatus;
  deletedAt?: string;
}

export interface IPayout {
  id: string;
  alert?: IAlert;
  approvalCheck: IPayoutApprovalCheck;
  cancelReason?: string;
  currency: CurrencyType;
  autoGratuity: number;
  disbursementMethod: DisbursementMethod;
  employee: IPayoutEmployee;
  expiresAt?: string;
  gross: number;
  holdback: number;
  job: IJob;
  net: number;
  notes?: string;
  paidAt?: string;
  paymentCategory?: string;
  paymentDate: string;
  paymentDestination?: ICardDetails;
  paymentId: string;
  refType?: PayoutRefType;
  refId?: string;
  site: IBaseSite;
  status: PayoutStatus;
  statusHistory?: IPayoutStatusHistory[];
  type: PaymentType;
  userId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface IPayoutApprovalCheck {
  status: PayoutApprovalStatus;
  statusDesc?: string;
  reviewers: {
    id: string;
    name: string;
    type: string;
    status: PayoutApprovalStatus;
  }[];
  failedChecks: {
    name: PayoutApprovalFailedCheckStatus;
    approvalRequired: boolean;
  }[];
}

export interface IReviewPayout {
  status?: PayoutApprovalStatus;
  desc: string;
}

export interface ICancelPayoutOptions {
  cancelReason: string;
}

export interface IPayoutStatusHistory {
  action: EventActionTypes;
  status: PayoutStatus;
  changedBy: ISubmittedBy;
  createdAt: string;
}

export interface IPayoutsPolicies {
  lastPayment?: ILastPaymentPolicies;
  dailyVelocityLimit?: IDailyVelocityLimitPolicies;
}

export enum ApprovalPoliciesStatusTypes {
  ENABLED = 'enabled',
  DISABLED = 'disabled',
}

interface IThresholdBasedPaymentPolicies {
  status: ApprovalPoliciesStatusTypes;
  threshold: number;
}

interface ILastPaymentPolicies extends IThresholdBasedPaymentPolicies {}

interface IDailyVelocityLimitPolicies extends IThresholdBasedPaymentPolicies {}
export interface IGetPayoutsStatusRequest {
  paymentIdTextSearch?: string | string[];
  statusSearch?: string | string[];
  payeeNameTextSearch?: string | string[];
  employeeIdSearch?: string | string[];
  siteEmployeeIdSearch?: string | string[];
  fundAccountType?: string | string[];
  disbursementMethod?: string | string[];
}

export interface IBasePayoutState {
  net: number;
  gross: number;
  holdbacks: number;
  count: number;
  instant: number;
  payroll: number;
  autoGratuity: number;
}

export interface IPayoutStateByDate extends IBasePayoutState {
  date: string;
}

export interface IPayoutStateByStatus extends IBasePayoutState {
  status: PayoutStatus;
}

export interface IPayoutStateByParticipant {
  autoGratuities: number;
  count: number;
  tips: number;
  total: number;
  name: string;
  type: CalculatorParticipantType;
}

export interface IPayoutStats {
  byDate: IPayoutStateByDate[];
  byStatus: IPayoutStateByStatus[];
  byParticipant: IPayoutStateByParticipant[];
}

export interface IEmployeePayrollTipStatusCount {
  paid: number;
  payroll: number;
  pending: number;
  canceled: number;
  expired: number;
}

export interface IEmployeePayrollTip {
  organizationId: string;
  siteId: string;
  paymentId: string;
  gross: number;
  holdback: number;
  net: number;
  direct: number;
  indirect: number;
  instant: { total: number; tips: number; autoGratuities: number };
  payroll: { total: number; tips: number; autoGratuities: number };
  statusCounts: IEmployeePayrollTipStatusCount;
  employee: IEmployee;
}

export interface IGetEmployeePayoutsResponse extends IApiList<IPayout> {
  employee: IEmployee;
}

export interface IPayPeriod {
  start: string;
  end: string;
}

export enum IPayPeriodFrequency {
  BI_WEEKLY = 'bi_weekly',
  SEMI_MONTHLY = 'semi_monthly',
  WEEKLY = 'weekly',
}

export enum IFirstPayDay {
  FIRST = '1',
  FIFTEENTH = '15',
}

export interface IGetPayPeriodsResponse {
  anchorDate: string;
  frequency: IPayPeriodFrequency;
  payDay1: IFirstPayDay;
  data: IPayPeriod[];
  rangeStart: string;
  rangeEnd: string;
}

export interface IGetPayPeriodsOptions {
  siteId: string;
  orgId: string;
}

export interface IGetPayPeriodsSuccessParams extends IGetPayPeriodsOptions {
  response: IGetPayPeriodsResponse;
}

export interface IExportPayout {
  id: string;
  custEmployeeId: string;
  disbursementMethod: DisbursementMethod;
  firstName: string;
  lastName: string;
  jobTitle: string;
  jobClassification: string;
  gross: number;
  holdback: number;
  net: number;
  autoGratuity: number;
  notes: string;
  paymentDate: string;
  siteName: string;
  status: PayoutStatus;
  submittedBy: string;
  createdAt: string;
  updatedAt: string;
}

export enum PayoutMethodTypes {
  STANDARD = 'standard',
  UPLOAD = 'upload',
  CALCULATOR = 'calculator',
}
